import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';

import { environment } from '../../environments/environment';
import { PwaUpdateService } from '../pwa-update.service';

@Component({
  selector: 'app-pwa-update-notification',
  standalone: true,
  imports: [CommonModule, ButtonModule, ToastModule],
  providers: [MessageService],
  templateUrl: './pwa-update-notification.component.html',
  styleUrls: ['./pwa-update-notification.component.scss'],
})
export class PwaUpdateNotificationComponent implements OnInit, OnDestroy {
  showUpdate = false;
  showForceCheckButton = false;
  environment = environment;

  private pwaUpdateService = inject(PwaUpdateService);
  private messageService = inject(MessageService);
  private router = inject(Router);
  private routerSubscription: Subscription | null = null;
  private updateSubscription: Subscription | null = null;

  private readonly allowedRoutes = ['/', '/auth/login'];

  ngOnInit() {
    this.checkRouteVisibility(this.router.url);

    this.routerSubscription = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
      this.checkRouteVisibility((event as NavigationEnd).urlAfterRedirects);
    });

    this.updateSubscription = this.pwaUpdateService.updateAvailable.subscribe((available) => {
      this.showUpdate = available;
      if (available) {
        this.messageService.add({
          key: 'update',
          severity: 'info',
          summary: 'Update Available',
          detail: 'A new version of the app is ready. Click Update to reload.',
          sticky: true,
        });
      }
    });
  }

  updateApp() {
    this.pwaUpdateService.activateUpdate();
  }

  dismissUpdate() {
    this.showUpdate = false;
    this.messageService.clear('update');
  }

  forceCheck() {
    this.pwaUpdateService
      .forceUpdateCheck()
      ?.then((updateAvailable) => {
        if (updateAvailable) {
          this.messageService.add({
            severity: 'success',
            summary: 'Update Found',
            detail: 'A new version is available!',
          });
        } else {
          this.messageService.add({
            severity: 'info',
            summary: 'No Updates',
            detail: 'You have the latest version.',
          });
        }
      })
      .catch((error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Check Failed',
          detail: 'Failed to check for updates.',
        });
        console.error('Update check failed:', error);
      });
  }

  ngOnDestroy() {
    this.routerSubscription?.unsubscribe();
    this.updateSubscription?.unsubscribe();
  }

  private checkRouteVisibility(url: string) {
    const path = url.split('?')[0];
    this.showForceCheckButton = this.allowedRoutes.includes(path);
  }
}
